/**
 * Creative Commons (CC) license identifiers and metadata for display and filtering.
 * Used by ProductManagement, ProductDetail, and Catalog.
 */

export const CC_LICENSE_OPTIONS = [
  { id: 'CC0', name: 'CC0 Public Domain', deedUrl: 'https://creativecommons.org/publicdomain/zero/1.0/', description: 'Public domain; no attribution required.' },
  { id: 'CC BY 4.0', name: 'CC BY 4.0', deedUrl: 'https://creativecommons.org/licenses/by/4.0/', description: 'Use with attribution; commercial and derivative works allowed.' },
  { id: 'CC BY-SA 4.0', name: 'CC BY-SA 4.0', deedUrl: 'https://creativecommons.org/licenses/by-sa/4.0/', description: 'Attribution + ShareAlike; derivatives must use same license.' },
  { id: 'CC BY-NC 4.0', name: 'CC BY-NC 4.0', deedUrl: 'https://creativecommons.org/licenses/by-nc/4.0/', description: 'Attribution + NonCommercial; no commercial use without permission.' },
  { id: 'CC BY-NC-SA 4.0', name: 'CC BY-NC-SA 4.0', deedUrl: 'https://creativecommons.org/licenses/by-nc-sa/4.0/', description: 'Attribution + NonCommercial + ShareAlike.' },
  { id: 'CC BY-ND 4.0', name: 'CC BY-ND 4.0', deedUrl: 'https://creativecommons.org/licenses/by-nd/4.0/', description: 'Attribution + NoDerivatives; no modifications allowed.' },
  { id: 'CC BY-NC-ND 4.0', name: 'CC BY-NC-ND 4.0', deedUrl: 'https://creativecommons.org/licenses/by-nc-nd/4.0/', description: 'Most restrictive; attribution, non-commercial, no derivatives.' },
  { id: 'Custom', name: 'Custom / Other', deedUrl: '', description: 'Creator-defined or commercial terms not covered by CC.' },
];

export const DEFAULT_LICENSE_BUILDER = {
  type: 'CC BY 4.0',
  commercialUse: true,
  redistribution: false,
  seatCount: 1,
  modificationsAllowed: true,
  terms: '',
  restrictions: [],
};

export function getLicenseOption(identifier) {
  if (!identifier) return null;
  return CC_LICENSE_OPTIONS.find(opt => opt.id === identifier) || null;
}

export function getLicenseDisplayName(license) {
  if (!license) return 'CC BY 4.0';
  const type = typeof license === 'object' ? license?.type : license;
  if (!type) return 'CC BY 4.0';
  const opt = getLicenseOption(type);
  return opt ? opt.name : type;
}

export function getLicenseDeedUrl(license) {
  if (!license) return '';
  const type = typeof license === 'object' ? license?.type : license;
  if (typeof license === 'object' && license?.deedUrl) return license.deedUrl;
  const opt = getLicenseOption(type);
  return opt ? opt.deedUrl : '';
}

export function buildLicenseSummary(license) {
  if (!license || typeof license !== 'object') return [];
  const parts = [];
  parts.push(license.commercialUse ? 'Commercial use allowed' : 'Non-commercial only');
  parts.push(license.redistribution ? 'Redistribution allowed' : 'No redistribution');
  if (license.seatCount > 1) parts.push(`Up to ${license.seatCount} seats`);
  else parts.push('Single seat');
  if (license.modificationsAllowed === false) parts.push('No modifications');
  return parts;
}

export function licenseFromBuilder(form) {
  const base = {
    type: form.type || 'CC BY 4.0',
    commercialUse: !!form.commercialUse,
    redistribution: !!form.redistribution,
    seatCount: Math.max(1, parseInt(form.seatCount, 10) || 1),
    modificationsAllowed: form.modificationsAllowed !== false,
    terms: form.terms || '',
    restrictions: form.restrictions || [],
  };
  const opt = getLicenseOption(base.type);
  if (opt) base.deedUrl = opt.deedUrl;
  if (base.type === 'Custom') base.name = 'Custom License';
  return base;
}

export function builderFromLicense(license) {
  if (!license || typeof license !== 'object') {
    return { ...DEFAULT_LICENSE_BUILDER };
  }
  return {
    type: license.type || 'CC BY 4.0',
    commercialUse: license.commercialUse !== false,
    redistribution: !!license.redistribution,
    seatCount: license.seatCount || 1,
    modificationsAllowed: license.modificationsAllowed !== false,
    terms: license.terms || '',
    restrictions: Array.isArray(license.restrictions) ? license.restrictions : [],
  };
}
